import { isNil } from 'lodash'
import { FilteredResult } from '../../../calculators/calculator-result-utility'
import { reduceToSum, reduceToSumOfItems } from '../../../util/array-utility'
import {
  formatEnergy,
  formatEnergyPerArea,
  formatPower,
  formatPowerPerArea,
} from '../../../util/formatters'
import type { ReportValuesRowProps } from './ReportValuesRow'

export type ReportCellValue = {
  area?: number
  energy?: number
  power?: number
  other?: string
  omit?: boolean
}

function createEnergyCellValues(
  energy: number,
  area?: number,
): ReportCellValue[] {
  return isNil(area) ? [{ energy }] : [{ energy }, { energy, area }]
}

function createPowerCellValues(
  power: number,
  area?: number,
): ReportCellValue[] {
  return isNil(area) ? [{ power }] : [{ power }, { power, area }]
}

function createOmittedCellValues(area?: number): ReportCellValue[] {
  return isNil(area) ? [{ omit: true }] : [{ omit: true }, { omit: true }]
}

export type ResultRowMapInput = Partial<FilteredResult> & {
  name: string
  highlightRow?: boolean
  area?: number
  otherValues?: ReportCellValue[]
}

export function mapNamedResultToValuesRow({
  name,
  total,
  max,
  area,
  highlightRow,
  otherValues = [],
}: ResultRowMapInput): ReportValuesRowProps {
  const totalValues = isNil(total)
    ? createOmittedCellValues(area)
    : createEnergyCellValues(total, area)

  const maxValues = isNil(max)
    ? createOmittedCellValues(area)
    : createPowerCellValues(max, area)

  return {
    title: name,
    values: [...totalValues, ...maxValues, ...otherValues],
    highlightRow,
  }
}

export type NamedResult = Omit<ResultRowMapInput, 'area'> & {
  result: FilteredResult
}

export type MapNamedResultsToRowsOptions = {
  totalRowName?: string
  maxRowName?: string
  area?: number
  totalOtherValues?: ReportCellValue[]
}

export function mapNamedResultsToRows(
  results: NamedResult[],
  options: MapNamedResultsToRowsOptions = {},
): ReportValuesRowProps[] {
  const { totalRowName, maxRowName, area, totalOtherValues } = options

  const rows = results.map(({ result, ...rest }) =>
    mapNamedResultToValuesRow({ ...result, ...rest, area }),
  )

  if (totalRowName) {
    const combinedTotal = results
      .map(({ result }) => result.total)
      .reduce(reduceToSum, 0)

    const totalRow = mapNamedResultToValuesRow({
      name: totalRowName,
      total: combinedTotal,
      area,
      otherValues: totalOtherValues,
      highlightRow: true,
    })

    rows.push(totalRow)
  }

  if (maxRowName) {
    const max = Math.max(
      ...results
        .map(({ result }) => result.values)
        .reduce(reduceToSumOfItems, []),
    )

    const maxRow = mapNamedResultToValuesRow({
      name: maxRowName,
      max,
      area,
      highlightRow: true,
    })

    rows.push(maxRow)
  }

  return rows
}

export function formatCellValue(cell: ReportCellValue): string {
  if (cell.omit) {
    return ''
  }
  if (!isNil(cell.energy)) {
    if (isNil(cell.area)) {
      return formatEnergy(cell.energy, { omitUnit: true })
    }
    return formatEnergyPerArea(cell.energy, cell.area, { omitUnit: true })
  }
  if (!isNil(cell.power)) {
    if (isNil(cell.area)) {
      return formatPower(cell.power, { omitUnit: true })
    }
    return formatPowerPerArea(cell.power, cell.area, { omitUnit: true })
  }
  if (cell.other) {
    return cell.other
  }
  return ''
}
